import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import { SMALL } from "constants/index"
import { FOLLOWABLE, FOLLOWING, FOLLOW_REQUESTED } from "constants/notifications"
import { useAppSelector } from "appRedux/hooks"
import { updateViewedUserProfile } from "appRedux/slices/viewedUserSlice"
import { isPublicPage } from "appRedux/baseQuery"
import {
  useCreateFollowRequestMutation,
  useCreateUnfollowRequestMutation,
  useCreateCancelFollowRequestMutation
} from "appRedux/slices/userApiSlice"
import CTA from "components/atoms/CTA"
import Dialog from "components/atoms/Dialog"
import WarningRoundedIcon from "components/atoms/_icons/WarningRoundedIcon"
import LoginRequiredDialog from "components/molecules/LoginRequiredDialog"

type FollowButtonProps = {
  userId: number;
  handle: string;
  status: FollowStatus;
  size?: "small" | "medium";
}

export default function FollowButton(props: FollowButtonProps) {
  const { userId, handle, status = FOLLOWABLE, size = SMALL } = props

  const [followStatus, setFollowStatus] = useState<FollowStatus>(status)
  const [showUnfollowConfirmationDialog, setShowUnfollowConfirmationDialog] = useState<boolean>(false)
  const [showLoginRequiredDialog, setShowLoginRequiredDialog] = useState<boolean>(false)

  const viewedUser = useAppSelector((state) => state.viewedUser)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [createFollowRequest, {
    isLoading: isFollowAcceptLoading,
    isSuccess: followRequestIsSuccess
  }] = useCreateFollowRequestMutation()

  const [createUnfollowRequest, {
    isLoading: isUnfollowRequestLoading,
    isSuccess: unfollowRequestIsSuccess
  }] = useCreateUnfollowRequestMutation()

  const [createCancelFollowRequest, {
    isLoading: isCancelFollowRequestLoading,
    isSuccess: cancelFollowRequestIsSuccess
  }] = useCreateCancelFollowRequestMutation()

  useEffect(() => {
    setFollowStatus(status)
  }, [status])

  useEffect(() => {
    if (!followRequestIsSuccess) return
    updateStates(FOLLOW_REQUESTED)
  }, [followRequestIsSuccess])

  useEffect(() => {
    if (!unfollowRequestIsSuccess) return
    updateStates(FOLLOWABLE)
    setShowUnfollowConfirmationDialog(false)
  }, [unfollowRequestIsSuccess])

  useEffect(() => {
    if (!cancelFollowRequestIsSuccess) return
    updateStates(FOLLOWABLE)
  }, [cancelFollowRequestIsSuccess])

  function updateStates(status: FollowStatus) {
    setFollowStatus(status)

    if (!viewedUser?.profile || userId !== viewedUser?.profile?.id) return
    dispatch(updateViewedUserProfile({
      ...viewedUser.profile,
      followStatus: status
    }))
  }

  function handleFollowUser() {
    if (isPublicPage()) {
      setShowLoginRequiredDialog(true)
      return
    }
    createFollowRequest(userId)
  }

  function handleCloseLoginRequiredDialog() {
    setShowLoginRequiredDialog(false)
  }

  function handleUnfollowUser() {
    createUnfollowRequest(userId)
  }

  function handleUnfollowButtonClicked() {
    setShowUnfollowConfirmationDialog(true)
  }

  function handleCancelFollowUser() {
    createCancelFollowRequest(userId)
  }

  function handleCloseUnfollowConfirmationDialog() {
    setShowUnfollowConfirmationDialog(false)
  }

  return (
    <Wrapper id="follow-button">
      {followStatus === FOLLOWABLE && <CTA
          variant="contained"
          variantColor="primaryDark"
          size={size}
          loading={isFollowAcceptLoading}
          onClick={handleFollowUser}
      >
        {t("connections.follow")}
      </CTA>}
      {followStatus === FOLLOW_REQUESTED && <CTA
          variant="contained"
          size={size}
          color="gray"
          loading={isCancelFollowRequestLoading}
          onClick={handleCancelFollowUser}
      >
        {t("general.pending")}
      </CTA>}
      {followStatus === FOLLOWING && <CTA
          variant="outlined"
          variantColor="primaryDark"
          size={size}
          loading={isUnfollowRequestLoading}
          onClick={handleUnfollowButtonClicked}
      >
        {t("connections.unfollow")}
      </CTA>}
      <Dialog
        message={t("connections.unfollowConfirmationQuestion", { handle })}
        icon={<WarningRoundedIcon/>}
        buttons={[
          {
            label: t("general.yes"),
            mode: "white",
            loading: isUnfollowRequestLoading,
            disabled: false,
            onClick: handleUnfollowUser
          },
          {
            label: t("general.no"),
            mode: "primary",
            loading: false,
            disabled: isUnfollowRequestLoading,
            onClick: handleCloseUnfollowConfirmationDialog
          }
        ]}
        open={showUnfollowConfirmationDialog}
        onClose={handleCloseUnfollowConfirmationDialog}
      />
      <LoginRequiredDialog
        open={showLoginRequiredDialog}
        message={t("auth.loginRequiredToFollow", { handle })}
        onClose={handleCloseLoginRequiredDialog}
      />
    </Wrapper>
  )
}


const Wrapper = styled("div")(({ theme }) => ({
  flexShrink: 0
}))
